import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { avatarSrc, formatApiError } from "../lib/api";
import { X, Search, Users, User as UserIcon, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function NewChatModal({ onClose, onCreated }) {
  const nav = useNavigate();
  const [mode, setMode] = useState("dm"); // dm | group
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef(null);

  const search = useCallback((val) => {
    if (!val.trim()) { setResults([]); return; }
    setSearching(true);
    api.get("/search/users", { params: { q: val } })
      .then(({ data }) => setResults(data))
      .catch(() => {})
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 220);
    return () => clearTimeout(timer.current);
  }, [q, search]);

  const toggle = (u) => {
    if (mode === "dm") { setSelected([u]); return; }
    setSelected((s) => s.find((x) => x.id === u.id) ? s.filter((x) => x.id !== u.id) : [...s, u]);
  };
  const isSel = (u) => selected.some((x) => x.id === u.id);

  const create = async () => {
    if (selected.length === 0) return toast.error("Pick at least one person");
    if (mode === "group" && !groupName.trim()) return toast.error("Name your group");
    setCreating(true);
    try {
      const { data } = await api.post("/conversations", {
        type: mode,
        member_ids: selected.map((s) => s.id),
        name: mode === "group" ? groupName.trim() : null,
      });
      onCreated && onCreated(data);
      onClose();
      nav(`/chat/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={onClose}>
      <div data-testid="new-chat-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0A0A0A] border border-[#1C1C1F] fade-up">
        <div className="flex items-center justify-between px-5 h-14 border-b border-[#1C1C1F]">
          <h2 className="font-heading uppercase tracking-widest text-sm text-white">New Conversation</h2>
          <button data-testid="new-chat-close" onClick={onClose} className="text-[#A1A1AA] hover:text-[#0066FF] transition-colors"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mode-dm" onClick={() => { setMode("dm"); setSelected([]); }} className={`flex items-center justify-center gap-2 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "dm" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}><UserIcon size={14} /> Direct</button>
            <button data-testid="mode-group" onClick={() => setMode("group")} className={`flex items-center justify-center gap-2 py-2.5 font-heading uppercase tracking-widest text-xs border transition-colors ${mode === "group" ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}><Users size={14} /> Group</button>
          </div>

          {mode === "group" && (
            <input data-testid="group-name-input" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm px-4 py-2.5 focus:outline-none focus:border-[#0066FF] rounded-xl" />
          )}

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map((u) => (
                <span key={u.id} className="inline-flex items-center gap-1 bg-[#0A2540] border border-[#0066FF] text-white px-2 py-1 font-mono text-[10px]">
                  @{u.username} <button onClick={() => toggle(u)} className="hover:text-[#0066FF]"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={15} />
            <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#0066FF] rounded-xl font-mono" />
          </div>

          <div className="max-h-56 overflow-y-auto border border-[#1C1C1F]" data-testid="user-search-results">
            {searching && <div className="flex items-center gap-2 px-4 py-3 text-[#52525B] font-mono text-xs"><Loader2 size={13} className="animate-spin" /> Searching…</div>}
            {!searching && q && results.length === 0 && <p className="px-4 py-3 text-[#52525B] font-mono text-xs">No users found.</p>}
            {results.map((u) => (
              <button key={u.id} data-testid={`user-result-${u.username}`} onClick={() => toggle(u)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isSel(u) ? "bg-[#0A2540]" : "hover:bg-[#141414]"}`}>
                {u.avatar_url ? <img src={avatarSrc(u.avatar_url)} alt={u.username} className="w-8 h-8 object-cover border border-[#2A2A2E]" /> : <div className="w-8 h-8 bg-[#0066FF] flex items-center justify-center font-heading text-white text-xs">{u.username[0].toUpperCase()}</div>}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm truncate">{u.display_name}</p>
                  <p className="font-mono text-[10px] text-[#52525B] truncate">@{u.username}</p>
                </div>
                {isSel(u) && <Check size={16} className="text-[#0066FF]" />}
              </button>
            ))}
          </div>

          <button data-testid="create-conversation-btn" onClick={create} disabled={creating} className="w-full bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm py-3 hover:bg-[#0055CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {creating ? <Loader2 size={16} className="animate-spin" /> : mode === "dm" ? "Start Chat" : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
}
